import {Button, Col, Container, Form, Modal, Row} from 'react-bootstrap';
import React, {ChangeEvent, useEffect, useState} from 'react';
import {FormattedMessage} from 'react-intl';
import PropTypes from 'prop-types';
import _ from 'lodash'
import {KeycloakProfile} from '../vendor/keycloak-js/keycloak';
import {api} from '../api';
import CallForPapersFormWidget, {initialValue, submissionValue} from './CallForPapersFormWidget';
import {Formik, FormikProps} from 'formik';
import {useHistory, useParams, withRouter} from 'react-router-dom';
import {
    CallForPaperForm,
    CallForPapers,
    EventReviewCriterium,
    FormWidget,
    PaperSubmission,
    PaperSubmissionFormDatum,
    PaperSubmissionReview, ReactRouterParamTypes,
    TaskProps
} from '../types/types';
import ReviewRadioGroupControl from "./widget/ReviewRadioGroupControl";
import {loadUserProfile} from "../auth";
import MyAreaButton from "./MyArea/MyAreaButton";

interface CallForPapersReviewFormProps {
    intl: any;
    callForPapers: CallForPapers;
    roles: any;
}

export interface CallForPapersReviewFormFields {
    widgets?: FormWidget[];
    tasks?: TaskProps[];
    review?: PaperSubmissionReview;
}

// Initialwerte einer Einreichung
const DefaultCallForPapersReviewFormInitialValues: CallForPapersReviewFormFields = {
    tasks: [],
    review: {
        reviewer: {
            mainContact: {
                salutation: "",
                firstName: "",
                lastName: "",
                street: "",
                houseno: "",
                postalCode: "",
                city: "",
                countryISOCode: "",
                email: "",
                phone: "",
                company: ""
            }
        },
        eventReviewConfiguration: {
            id: "",
            event: {
                id: ""
            },
            criteria: []
        },
        criteriaScores: [],
        reasoning: ""
    }
}

/**
 * Darstellung einer Einreichung in einer modifizierten, nicht veränderbaren Ansicht der CallForPapersForms mit
 * Bewertungsformular am Ende der Seite.
 *
 * @constructor
 */
const CallForPapersReviewForm = ({intl, callForPapers, roles}: CallForPapersReviewFormProps) => {
    const {submissionId, reviewId} = useParams<ReactRouterParamTypes>();
    const [submission, setSubmission] = useState<PaperSubmission>();
    const [review, setReview] = useState<PaperSubmissionReview>();
    const [reviewer, setReviewer] = useState<KeycloakProfile>();

    const initialValues = createInitialValues(callForPapers, submission, review);
    const isEdit = !!submission;
    const history = useHistory();

    const [showModal, setShowModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [eventReviewCriteria, setEventReviewCriteria] = useState<EventReviewCriterium[]>([]);

    useEffect(() => {
        loadUserProfile().then((userProfile: KeycloakProfile) => {
            setReviewer(userProfile);
        });

        const loadSubmissionReview = async () => {
            if (reviewId) {
                return await api.fetchSubmissionReview(reviewId);
            }
        }

        const loadPaperSubmissionReviewConfiguration = async (callForPapersId: string | undefined) => {
            return await api.fetchReviewConfigurationForCallForPapers(callForPapersId);
        }

        loadSubmissionReview().then((submissionReview: PaperSubmissionReview) => {
            setReview(submissionReview);
            setSubmission(submissionReview.submission);
            loadPaperSubmissionReviewConfiguration(submissionReview?.submission?.callForPapers?.id).then(eventReviewConfigPayload => {
                setEventReviewCriteria(eventReviewConfigPayload?.criteria);
            })
        });

    }, [submissionId, reviewId])

    /**
     * Submit-Handler, welcher an Formiks handleChange-prop übergeben wird
     *
     * @param values
     */
    const handleSubmit: (values: CallForPapersReviewFormFields) => void = (values: CallForPapersReviewFormFields) => {
        const reviewData = {
            ...values.review,
            reviewer: reviewer,
            submission: submission
        };

        setIsSubmitting(true);
        if (review?.id) {
            api.updateSubmissionReview(submission?.id, review.id, reviewData)
                .then(res => {
                    history.push(`/${callForPapers.urlSlug}/reviewresult`);
                })
                .catch((error) => {
                    setIsSubmitting(false);

                });
        }
    };

    /**
     * Rendert ein Modal für das Ablehnen einer Bewertung aus Gründen
     *
     * @param formik
     */
    const renderModal = (formik: FormikProps<CallForPapersReviewFormFields>) => {
        /**
         * @param event
         */
        const handleReasoningChange = (event: ChangeEvent<HTMLInputElement>) => {
            formik.setFieldValue('review.reasoning', event.target.value);
        }

        /**
         * Handler-Methode, welche die Ablehnung einer Bewertung eines Bewerters behandelt
         */
        const handleDecline = () => {
            const reviewData = {
                ...formik.values.review,
                reviewer: reviewer,
                submission: submission,
                language: intl.locale
            };

            if (review?.id) {
                api.updateSubmissionReview(submission?.id, review.id, reviewData)
                    .then(res => {
                        history.push(`/${callForPapers.urlSlug}/result`);
                    })
                    .catch(() => {
                        setIsSubmitting(false);
                    });
            }
        }

        return (
            <Modal
                show={showModal}
                onHide={() => setShowModal(false)}
                aria-labelledby="contained-modal-title-lg"
                size="lg"
                enforceFocus={false}
            >
                <Modal.Header closeButton>
                    <Modal.Title id="contained-modal-title-lg">
                        <FormattedMessage id="submission.review.decline.header"/>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p><FormattedMessage id="submission.review.decline.hint"/></p>
                    <Form.Group>
                        <Form.Label><FormattedMessage id="submission.review.decline.reasoning.header"/></Form.Label>
                        <Form.Control as="textarea"
                                      rows={2}
                                      name="declineReviewReason"
                                      value={formik.values.review?.reasoning}
                                      onChange={handleReasoningChange}
                        />
                        <span style={{color: "#ff0000"}}>{formik.errors.review}</span>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={() => setShowModal(false)}><FormattedMessage id="cancel"/></Button>
                    <Button variant="primary" onClick={handleDecline}><FormattedMessage id="save"/></Button>
                </Modal.Footer>
            </Modal>
        );
    }

    /**
     * Prüft ob die Bewertungsdeadline erreicht wurde
     *
     * @return {boolean}
     */
    const isAfterDeadline: () => boolean = () => {
        return new Date().getTime() > new Date(callForPapers.reviewDeadline).getTime();
    }

    /**
     * Rendert alle Widgets des Formulars
     *
     * @param formikProps
     */
    const renderWidgets: (formikProps: FormikProps<any>) => JSX.Element[] = (formikProps: FormikProps<any>) => {
        return _.get(callForPapers, 'form.widgets', [])
            .filter((widget: any) => widget.active && widget.isVisibleForReview) // Nur aktive und darzustellende Widgets sollen angezeigt werden
            .map((widget: any) => <CallForPapersFormWidget
                key={widget.id}
                widget={widget}
                disabled={true} // Alle Widgets hier sind disabled, damit Bewerter nichts ändern können
                formikProps={formikProps}
                callForPapers={callForPapers}
                submission={submission}
                roles={roles}/>
            );
    }

    /**
     * Rendert die Überschrift für wenn eine Einreichung existiert und man somit im /edit-mode ist
     *
     * @returns {JSX.Element}
     */
    const renderHeader: () => JSX.Element | null = () => {
        if (isEdit) {
            return (
                <Row>
                    <h2>
                        <FormattedMessage id="submission.form.header.review.mode"/>
                        <em>{callForPapers.versionContainer.event.name}</em>
                    </h2>
                    <p>
                        <FormattedMessage id="submission.form.header.review.deadline"/>
                        <em>{new Date(callForPapers.reviewDeadline).toLocaleDateString()}</em>
                    </p>
                </Row>
            );
        }
        return null;
    }

    /**
     * Rendert den Bewertungsblock einer Einreichung
     *
     * @param formikProps
     */
    const renderReviewBlock: (formikProps: FormikProps<any>) => JSX.Element = (formikProps: FormikProps<any>) => {
        return (
            <>
                <Row>
                    <h2>
                        <FormattedMessage id="submission.review.header"/>
                    </h2>
                    {
                        eventReviewCriteria.length === 0 &&
                        <h3 className="no-criteria criteria-margin">
                            <FormattedMessage id="submission.review.no_review_criteria"/>
                        </h3>
                    }
                    {
                        eventReviewCriteria.length > 0 &&
                        <Col sm={{span: 12}} className="criteria-margin">
                            <Row>
                                <Col sm={{offset: 4}}>
                                    <FormattedMessage id="submission.review.scoring.label.best"/>
                                </Col>
                                <Col sm={{offset: 2}}>
                                    <FormattedMessage id="submission.review.scoring.label.worst"/>
                                </Col>
                            </Row>
                            {eventReviewCriteria.map((criterium: EventReviewCriterium, key: number) =>
                                <ReviewRadioGroupControl criterium={criterium} key={key}/>
                            )}
                        </Col>
                    }
                </Row>
                <div className="criteria-margin">
                    <Button type="button" variant="danger" className="pull-left" onClick={() => setShowModal(true)}>
                        <FormattedMessage id="submission.review.button.decline"/>
                    </Button>

                    <Button type="button" variant="primary" className="pull-right"
                            disabled={isAfterDeadline() || isSubmitting}
                            onClick={() => handleSubmit(formikProps.values)}
                    >
                        <FormattedMessage id="submission.review.button.submit"/>
                    </Button>
                </div>
            </>
        );
    }

    // Haupt-Render der Funcional Component
    return (
        <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            onSubmit={handleSubmit}
        >
            {formikProps => (
                <Container>
                    <MyAreaButton/>
                    {renderHeader()}
                    {renderWidgets(formikProps)}

                    <hr/>
                    {renderReviewBlock(formikProps)}
                    {renderModal(formikProps)}
                </Container>
            )}
        </Formik>
    );

}

/**
 * TODO exportier die Funktion aus dem normalen Form und dekorier das hier mit der review
 *
 * Erzeugt die Initialen Werte der Formularfelder. Hierbei wird differenziert zwischen einer neuen Einreichung, bei der
 * alle Felder leer initialisiert werden, oder einer bestehenden Einreichung, bei der die alten Werte übernommen werden.
 *
 * @param callForPapers
 * @param submission
 * @param review
 */
const createInitialValues: (callForPapers: CallForPapers, submission?: PaperSubmission, review?: PaperSubmissionReview)
    => CallForPapersReviewFormFields = (callForPapers: CallForPapers, submission?: PaperSubmission, review?: PaperSubmissionReview) => {
    const form: CallForPaperForm = _.get(callForPapers, 'form', {id: '', allowPartialSubmissions: true, widgets: []});

    const urlParams = new URLSearchParams(window.location.search);
    const widgetValues: FormWidget[] = _.reduce(form.widgets, (dataShape: any, widget: FormWidget) => {
        dataShape[widget.id] = initialValue(widget, urlParams);
        return dataShape;
    }, {} as FormWidget);

    // Wenn eine Einreichung gegeben ist, Daten initialisieren!
    if (submission) {
        const filledWidgetValues: FormWidget[] = _.reduce(submission.formData, (dataShape: any, widget: PaperSubmissionFormDatum) => {
            dataShape[widget.id] = widget.value;
            return dataShape;
        }, {} as PaperSubmissionFormDatum);

        const submissionValues: any = _.transform(callForPapers.form.widgets, (dataShape: any, widget: FormWidget) => {
            const value: any = submissionValue(callForPapers, widget, submission)
            if (value !== undefined) {
                dataShape[widget.id] = value
            }
        }, {} as any)

        return {
            widgets: {
                ...submissionValues,
                ...filledWidgetValues
            },
            review: review ?? DefaultCallForPapersReviewFormInitialValues.review,
            tasks: submission.tasks ?? []
        };
    }

    DefaultCallForPapersReviewFormInitialValues.widgets = widgetValues;
    return DefaultCallForPapersReviewFormInitialValues;
}

CallForPapersReviewForm.propTypes = {
    callForPapers: PropTypes.object
};

// FIXME typehinting ordentlich...
// @ts-ignore
export default withRouter(CallForPapersReviewForm);
